import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import Footer from "../components/Footer";
import heroImg from "../assets/hero.jpg";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

const CareGuidePage = () => {
  const [careTips, setCareTips] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [expertAdvice, setExpertAdvice] = useState("");
  const [expertAuthor, setExpertAuthor] = useState(
    "L'équipe SAFA, passionnée par l'artisanat marocain"
  );
  const [heroTitle, setHeroTitle] = useState("");
  const [heroSubtitle, setHeroSubtitle] = useState("");
  const [introText, setIntroText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${apiUrl}/api/care-guide`)
      .then((response) => {
        const data = response.data;
        setCareTips(data.careTips || []);
        setMaterials(data.materials || []);
        setExpertAdvice(data.expertAdvice || "");
        setExpertAuthor(
          data.expertAuthor ||
            "L'équipe SAFA, passionnée par l'artisanat marocain"
        );
        setHeroTitle(data.heroTitle || "L'Art de Préserver Vos Babouches SAFA");
        setHeroSubtitle(
          data.heroSubtitle ||
            "Découvrez les secrets de SAFA Shop pour entretenir vos babouches marocaines"
        );
        setIntroText(
          data.introText ||
            "Les babouches SAFA sont le fruit d'un artisanat millénaire."
        );
        setLoading(false);
      })
      .catch((err) => {
        console.error("Erreur lors du fetch des données:", err);
        setError("Erreur lors du chargement des données");
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
        </div>
        <p className="loading-text">Chargement en cours...</p>
      </div>
    );
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="care-guide-container">
      {/* Hero Section with Parallax Effect */}
      <motion.div
        className="hero-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
      >
        <div className="hero-overlay" />
        <motion.div
          className="hero-content"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          <h1>{heroTitle}</h1>
          <motion.div
            className="divider"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          />
          <p>{heroSubtitle}</p>
        </motion.div>
        <motion.div
          className="scroll-hint"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          onClick={() => {
            window.scrollTo({
              top: window.innerHeight,
              behavior: "smooth",
            });
          }}
          style={{ cursor: "pointer" }}
        >
          <span>Découvrir</span>
        </motion.div>
      </motion.div>

      {/* Intro Section */}
      <section className="intro-section">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
          className="intro-content"
        >
          <h2>
            Savoir-Faire <span className="highlight">Traditionnel SAFA</span>
          </h2>
          <div className="ornamental-divider">
            <div className="line" />
            <div className="decorative-element">✻</div>
            <div className="line" />
          </div>
          <p>{introText}</p>
          <motion.div className="signature" whileHover={{ scale: 1.05 }}>
            <span>L'équipe SAFA</span>
          </motion.div>
        </motion.div>
      </section>

      {/* Care Tips Section */}
      <section className="care-tips-section">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="section-header"
        >
          <h2>
            Nos <span className="care-highlight">Conseils d'Entretien</span>
          </h2>
          <p>Les secrets pour préserver la beauté de vos babouches SAFA</p>
        </motion.div>

        <div className="tips-grid">
          {careTips.map((item, index) => (
            <motion.div
              key={index}
              className="tip-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              {item.imageUrl && (
                <div className="tip-image-container">
                  <img
                    src={`${apiUrl}${item.imageUrl}`}
                    alt={`Illustration ${item.title}`}
                    className="tip-image"
                  />
                </div>
              )}
              <div className="tip-content">
                <h3>{item.title}</h3>
                <ul className="tip-list">
                  {item.tips.map((tip, i) => (
                    <li key={i}>{tip}</li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Materials Section */}
      <section className="materials-section">
        <motion.div
          className="materials-content"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        >
          <h2>
            Entretien des <span className="materials-highlight">Matériaux</span>
          </h2>
          <p>
            Chaque matériau utilisé dans nos babouches SAFA nécessite un soin
            particulier. Découvrez nos recommandations pour maintenir la qualité
            et la beauté de vos babouches dans le temps.
          </p>

          <div className="materials-grid">
            {materials.map((material, index) => (
              <motion.div
                key={index}
                className="material-card"
                whileHover={{ scale: 1.05 }}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="material-icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </div>
                <h3>{material.name}</h3>
                <p>{material.care}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Expert Advice Section */}
      <section className="expert-section">
        <motion.div
          className="expert-card"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2>
            Conseil d'<span className="expert-highlight">Expert</span>
          </h2>
          <p>
            {expertAdvice ||
              `"Pour les babouches SAFA en cuir, appliquez une petite quantité de cire d'abeille une fois par mois."`}
          </p>
          <div className="expert-signature">
            <span>
              {expertAuthor ||
                "L'équipe SAFA, passionnée par l'artisanat marocain"}
            </span>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default CareGuidePage;

// CSS moderne inspiré d'AboutPage
const styles = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800;900&family=Cormorant+Garamond:wght@300;400;500;600;700&family=Inter:wght@300;400;500;600;700&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  font-family: 'Cormorant Garamond', serif;
  background: linear-gradient(135deg, #f8f5f0 0%, #f0ebe0 100%);
}

.care-guide-container {
  font-family: 'Cormorant Garamond', serif;
  color: #2c1810;
  margin: 0 auto;
  overflow-x: hidden;
  background: linear-gradient(135deg, #f8f5f0 0%, #f0ebe0 100%);
}

/* Loading Styles */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  padding: 2rem;
  font-family: 'Cormorant Garamond', serif;
  position: relative;
  z-index: 2;
}

.loading-spinner {
  position: relative;
  width: 60px;
  height: 60px;
  margin-bottom: 1rem;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-radius: 50%;
  animation: spin 1.5s linear infinite;
}

.spinner-ring:nth-child(1) {
  border-top: 3px solid #d4af37;
}

.spinner-ring:nth-child(2) {
  border-right: 3px solid #2c1810;
  animation-delay: 0.5s;
}

.spinner-ring:nth-child(3) {
  border-bottom: 3px solid #5a4a3a;
  animation-delay: 1s;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  color: #2c1810;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  font-family: 'Cormorant Garamond', serif;
}

.error-message {
  font-family: 'Cormorant Garamond', serif;
  color: #d32f2f;
  text-align: center;
  font-size: 18px;
  padding: 40px;
  position: relative;
  z-index: 2;
}

/* Hero Section */
.hero-section {
  position: relative;
  height: 100vh;
  min-height: 600px;
  background-image: linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${heroImg});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  margin-bottom: 60px;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(5px);
}

.hero-content {
  position: relative;
  z-index: 2;
  padding: 0 20px;
}

.hero-content h1 {
  font-size: 3.5rem;
  font-weight: 600;
  margin-bottom: 20px;
  letter-spacing: 1px;
  text-shadow: 2px 2px 8px rgba(0,0,0,0.6);
  font-family: 'Cormorant Garamond', serif;
}

.hero-content p {
  font-size: 1.3rem;
  font-style: italic;
  text-shadow: 1px 1px 4px rgba(0,0,0,0.5);
  font-family: 'Cormorant Garamond', serif;
}

.divider {
  height: 2px;
  width: 100px;
  background: #d4af37;
  margin: 25px auto;
  transform-origin: center;
}

.scroll-hint {
  position: absolute;
  bottom: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}

.scroll-hint span {
  font-size: 14px;
  margin-bottom: 5px;
  font-family: 'Cormorant Garamond', serif;
}

.scroll-hint::after {
  content: "";
  width: 20px;
  height: 30px;
  border: 2px solid white;
  border-radius: 10px;
}

.scroll-hint::before {
  content: "";
  width: 4px;
  height: 8px;
  background: white;
  border-radius: 2px;
  position: absolute;
  bottom: 8px;
  animation: scrollHint 2s infinite;
}

@keyframes scrollHint {
  0% { transform: translateY(0); opacity: 0; }
  50% { transform: translateY(5px); opacity: 1; }
  100% { transform: translateY(10px); opacity: 0; }
}

/* Intro Section */
.intro-section {
  padding: 20px 20px !important;
  text-align: center;
  position: relative;
  margin-bottom: 80px;
}

.intro-content {
  max-width: 900px;
  margin: 0 auto;
}

.intro-content h2 {
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 30px;
  color: #2c1810;
  font-family: 'Cormorant Garamond', serif;
}

.highlight {
  color: #d4af37;
  font-weight: 600;
  position: relative;
}

.highlight::after {
  content: "";
  position: absolute;
  bottom: 5px;
  left: 0;
  width: 100%;
  height: 8px;
  background: rgba(212, 175, 55, 0.2);
  z-index: -1;
}

.ornamental-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 30px 0;
}

.ornamental-divider .line {
  height: 1px;
  width: 100px;
  background: linear-gradient(to right, transparent, #d4af37, transparent);
}

.ornamental-divider .decorative-element {
  margin: 0 15px;
  color: #d4af37;
  font-size: 1.5rem;
}

.intro-content p {
  font-size: 18px;
  line-height: 1.8;
  margin-bottom: 40px;
  color: #5a4a3a;
  font-family: 'Cormorant Garamond', serif;
}

.signature {
  display: inline-block;
  font-style: italic;
  font-size: 16px;
  padding: 10px 30px;
  border-top: 1px solid #d4af37;
  cursor: default;
  color: #2c1810;
  font-family: 'Cormorant Garamond', serif;
}

/* Care Tips Section */
.care-tips-section {
  padding: 80px 20px;
  text-align: center;
  margin-bottom: 80px;
}

.section-header h2 {
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 15px;
  color: #2c1810;
  font-family: 'Cormorant Garamond', serif;
}

.care-highlight {
  position: relative;
}

.care-highlight::after {
  content: "";
  position: absolute;
  bottom: 5px;
  left: 0;
  width: 100%;
  height: 12px;
  background: rgba(212, 175, 55, 0.2);
  z-index: -1;
}

.section-header p {
  font-size: 16px;
  color: #5a4a3a;
  margin-bottom: 50px;
  font-style: italic;
  font-family: 'Cormorant Garamond', serif;
}

.tips-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 35px;
  max-width: 1200px;
  margin: 0 auto;
}

.tip-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  padding: 35px 25px;
  border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  cursor: default;
  border: 1px solid rgba(212, 175, 55, 0.2);
}

.tip-card:hover {
  box-shadow: 0 8px 25px rgba(212, 175, 55, 0.2);
  transform: translateY(-5px);
  border: 1px solid rgba(212, 175, 55, 0.3);
}

.tip-image-container {
  margin-bottom: 25px;
  border-radius: 15px;
  overflow: hidden;
}

.tip-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.tip-card:hover .tip-image {
  transform: scale(1.05);
}

.tip-content h3 {
  font-size: 1.3rem;
  margin-bottom: 20px;
  color: #2c1810;
  font-weight: 600;
  font-family: 'Cormorant Garamond', serif;
}

.tip-list {
  list-style: none;
  padding: 0;
  text-align: left;
}

.tip-list li {
  position: relative;
  padding-left: 25px;
  margin-bottom: 12px;
  font-size: 16px;
  line-height: 1.6;
  color: #5a4a3a;
  font-family: 'Cormorant Garamond', serif;
}

.tip-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #d4af37;
  font-size: 1.5rem;
}

/* Materials Section */
.materials-section {
  padding: 80px 20px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  margin-bottom: 80px;
  border: 1px solid rgba(212, 175, 55, 0.2);
}

.materials-content {
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
}

.materials-content h2 {
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 30px;
  color: #2c1810;
  font-family: 'Cormorant Garamond', serif;
}

.materials-highlight {
  position: relative;
}

.materials-highlight::after {
  content: "";
  position: absolute;
  bottom: 5px;
  left: 0;
  width: 100%;
  height: 12px;
  background: rgba(212, 175, 55, 0.2);
  z-index: -1;
}

.materials-content p {
  font-size: 16px;
  line-height: 1.8;
  margin-bottom: 50px;
  color: #5a4a3a;
  font-family: 'Cormorant Garamond', serif;
}

.materials-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 35px;
  max-width: 900px;
  margin: 0 auto;
}

.material-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  padding: 35px 25px;
  border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(212, 175, 55, 0.2);
}

.material-card:hover {
  box-shadow: 0 8px 25px rgba(212, 175, 55, 0.2);
  transform: translateY(-5px);
  border: 1px solid rgba(212, 175, 55, 0.3);
}

.material-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(212, 175, 55, 0.2);
  border-radius: 50%;
  color: #2c1810;
  border: 1px solid rgba(212, 175, 55, 0.3);
}

.material-card h3 {
  font-size: 1.3rem;
  color: #2c1810;
  margin-bottom: 15px;
  font-weight: 600;
  font-family: 'Cormorant Garamond', serif;
}

.material-card p {
  font-style: italic;
  color: #5a4a3a;
  line-height: 1.6;
  font-size: 16px;
  font-family: 'Cormorant Garamond', serif;
}

/* Responsive pour mobile */
@media (max-width: 768px) {
  .materials-grid {
    grid-template-columns: 1fr;
    gap: 25px;
  }
  
  .material-card {
    padding: 25px 20px;
  }
}

/* Expert Section */
.expert-section {
  position: relative;
  padding: 80px 20px;
  margin-bottom: 80px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  overflow: hidden;
  border: 1px solid rgba(212, 175, 55, 0.2);
}

.expert-section::before {
  position: absolute;
  top: 0;
  right: 0;
  width: 40%;
  height: 100vh;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><path fill="%23d4af37" fill-opacity="0.03" d="M50,0 C60,20 80,30 100,50 C80,70 60,80 50,100 C40,80 20,70 0,50 C20,30 40,20 50,0 Z"></path></svg>');
  background-size: 150px;
  opacity: 0.6;
}

.expert-card {
  position: relative;
  z-index: 2;
  max-width: 700px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  padding: 40px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  border: 1px solid rgba(212, 175, 55, 0.3);
  margin: 0 auto;
  border-radius: 20px;
}

.expert-card h2 {
  font-size: 2.2rem;
  font-weight: 600;
  margin-bottom: 30px;
  color: #2c1810;
  font-family: 'Cormorant Garamond', serif;
}

.expert-highlight {
  position: relative;
}

.expert-highlight::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 12px;
  background: rgba(212, 175, 55, 0.2);
  z-index: -1;
}

.expert-card p {
  font-size: 18px;
  line-height: 1.8;
  margin-bottom: 30px;
  font-style: italic;
  color: #5a4a3a;
  font-family: 'Cormorant Garamond', serif;
}

.expert-signature {
  display: block;
  margin-top: 20px;
  font-weight: 600;
  color: #2c1810;
  font-size: 16px;
  font-style: italic;
  font-family: 'Cormorant Garamond', serif;
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-content h1 {
    font-size: 2.5rem;
  }
  
  .hero-content p {
    font-size: 1.1rem;
  }
  
  .intro-content h2, .section-header h2, .materials-content h2, .expert-card h2 {
    font-size: 2rem;
  }
  
  .tips-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  
  .materials-grid {
    grid-template-columns: 1fr;
    gap: 25px;
  }
  
  .expert-card {
    padding: 30px 20px;
    width: 100%;
    box-sizing: border-box;
  }
  
  .tip-content {
    padding: 20px;
  }
  
  .material-card {
    padding: 30px 20px;
  }
}

@media (max-width: 480px) {
  .hero-content h1 {
    font-size: 2rem;
    letter-spacing: 1px;
  }
  
  .hero-content p {
    font-size: 1rem;
  }
}
`;

// Inject styles
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);
